import { useCallback, useMemo, useSyncExternalStore } from 'react';

import { useViewer } from '@/entities/viewer';

/**
 * The calendar SOURCES of the GLOBAL calendar (Home preview + /app/calendar): one selection of
 * boards, remembered per user in this browser. It is stored as the boards switched OFF, so a board
 * created later is shown until the person hides it. Board / workspace calendars and the planner
 * have their own, independent state and never read this.
 */
const PREFIX = 'zadok.calendarSources.';

// Fallback for a blocked storage: the choice is then shared within this tab only.
const memory = new Map<string, string>();
const listeners = new Set<() => void>();

function readRaw(key: string): string {
  try {
    return window.localStorage.getItem(key) ?? '';
  } catch {
    return memory.get(key) ?? '';
  }
}

function writeRaw(key: string, raw: string): void {
  try {
    if (raw) window.localStorage.setItem(key, raw);
    else window.localStorage.removeItem(key);
  } catch {
    memory.set(key, raw); // storage unavailable: kept for this tab only
  }
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = () => {
    listener();
  };
  window.addEventListener('storage', onStorage); // another tab changed the choice
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

function parse(raw: string): string[] {
  try {
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

export function sameIds(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}

export interface SharedSources {
  /** Boards switched off (may name boards that no longer exist: filter with the known ones). */
  hidden: readonly string[];
  remember: (hidden: readonly string[]) => void;
}

/** The shared selection of the signed-in user (stored under their id, never across accounts). */
export function useSharedSources(): SharedSources {
  const viewer = useViewer();
  const key = `${PREFIX}${viewer.status === 'authenticated' ? viewer.user.id : 'anonymous'}`;
  const raw = useSyncExternalStore(
    subscribe,
    () => readRaw(key),
    () => '',
  );
  const hidden = useMemo(() => parse(raw), [raw]);
  const remember = useCallback(
    (ids: readonly string[]) => {
      const next = [...new Set(ids)].sort();
      writeRaw(key, next.length > 0 ? JSON.stringify(next) : '');
    },
    [key],
  );
  return useMemo(() => ({ hidden, remember }), [hidden, remember]);
}

// --- pure helpers -------------------------------------------------------------

export interface SourceBoard {
  id: string;
  name: string;
  color: string | null;
  workspace_id: string | null;
}

export interface SourceGroup<B extends SourceBoard = SourceBoard> {
  /** The workspace id ('' = boards without a workspace). */
  id: string;
  name: string;
  items: B[];
}

/** Boards grouped by workspace IDENTITY (two workspaces may share a name), in list order. */
export function groupSources<B extends SourceBoard>(
  boards: readonly B[],
  workspaceNames: ReadonlyMap<string, string>,
  fallbackName: string,
): SourceGroup<B>[] {
  const byWorkspace = new Map<string, B[]>();
  for (const board of boards) {
    const key = board.workspace_id ?? '';
    byWorkspace.set(key, [...(byWorkspace.get(key) ?? []), board]);
  }
  return [...byWorkspace].map(([id, items]) => ({
    id,
    name: id ? (workspaceNames.get(id) ?? '') : fallbackName,
    items,
  }));
}

/** Keeps the groups / boards matching a query (a matching workspace keeps all its boards). */
export function searchSources<B extends SourceBoard>(
  groups: readonly SourceGroup<B>[],
  query: string,
): SourceGroup<B>[] {
  const needle = query.trim().toLocaleLowerCase();
  if (!needle) return [...groups];
  return groups.flatMap((group) => {
    if (group.name.toLocaleLowerCase().includes(needle)) return [group];
    const items = group.items.filter((board) => board.name.toLocaleLowerCase().includes(needle));
    return items.length > 0 ? [{ ...group, items }] : [];
  });
}

/** The hidden ids that still name an available board. */
export function knownHidden(
  hidden: readonly string[],
  boards: readonly { id: string }[],
): string[] {
  const ids = new Set(boards.map((board) => board.id));
  return hidden.filter((id) => ids.has(id));
}

export function workspaceNamesOf(
  boards: readonly { workspace_id: string | null; workspace_name?: string | null }[],
): Map<string, string> {
  const names = new Map<string, string>();
  for (const board of boards) {
    if (board.workspace_id && board.workspace_name)
      names.set(board.workspace_id, board.workspace_name);
  }
  return names;
}
